import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/league-formats')({
    component: RouteComponent,
})

// Closest existing destination per format: league ranking types live in the league software,
// ladders have their own generator, round robin/social formats have dedicated generators.
const LMS = "/sports-league-management-software/";
const LADDER = "/ladder-league-generator/";
const RR = "/round-robin-tournament-generator/";
const AMERICANO = "/americano-generator/";
const MEX = "/mexicano-tournament-generator/";

type Format = {code: string; name: string; blurb: string; cta: string; to: string};

const FORMATS: Format[] = [
    {code: "PTS", name: "Points League", blurb: "Classic standings ranked by points earned — e.g. 3 for a win, 1 for a draw.", cta: "Build league", to: LMS},
    {code: "W%", name: "Win % League", blurb: "Rank by win percentage so standings stay fair when players play different numbers of games.", cta: "Build league", to: LMS},
    {code: "LAD", name: "Ladder League", blurb: "Players climb a ranking ladder by winning challenges or scheduled matches.", cta: "Create ladder", to: LADDER},
    {code: "DIV", name: "Division Leagues", blurb: "Split a large membership into divisions with promotion and relegation between seasons.", cta: "Build league", to: LMS},
    {code: "BOX", name: "Box League", blurb: "Small boxes of similar-level players; winners move up and others move down each cycle.", cta: "Build league", to: LMS},
    {code: "RR", name: "Round Robin League", blurb: "Everyone plays everyone for a fair, complete standings table.", cta: "Generate fixtures", to: RR},
    {code: "A", name: "Americano League", blurb: "A rotating social league for padel, tennis and pickleball club nights.", cta: "Generate Americano", to: AMERICANO},
    {code: "M", name: "Mexicano League", blurb: "An adaptive social league that balances matchups based on player results.", cta: "Generate Mexicano", to: MEX},
];

type Compare = {name: string; fairness: number; volume: string; bestFor: string; to: string};

const COMPARE: Compare[] = [
    {name: "Points League", fairness: 5, volume: "High", bestFor: "Traditional season-long clubs and teams", to: LMS},
    {name: "Win % League", fairness: 5, volume: "Flexible", bestFor: "Leagues where players play unequal games", to: LMS},
    {name: "Ladder League", fairness: 4, volume: "Flexible", bestFor: "Ongoing engagement and live rankings", to: LADDER},
    {name: "Division Leagues", fairness: 4, volume: "High", bestFor: "Large memberships across skill levels", to: LMS},
    {name: "Box League", fairness: 4, volume: "Medium", bestFor: "Ongoing club play with regular reshuffles", to: LMS},
    {name: "Round Robin League", fairness: 5, volume: "Medium", bestFor: "Small groups wanting a fair, complete table", to: RR},
];

type Guide = {tag: string; name: string; desc: string; ctaLabel: string; to: string; steps: string[]; bestFor: string; pros: string[]; cons: string[]};

const GUIDES: Guide[] = [
    {
        tag: "Classic standings", name: "Points League", to: LMS, ctaLabel: "Build a League",
        desc: "A points league ranks teams or players by total points earned — for example three points for a win and one for a draw. The standings table is the classic way to run a season.",
        steps: [
            "Players or teams are added to the league.",
            "Fixtures are generated across the season.",
            "Each result awards points (e.g. 3 for a win, 1 for a draw).",
            "The team with the most points tops the table.",
        ],
        bestFor: "Traditional club and team leagues that play a full or fixed fixture list across a season.",
        pros: ["Familiar and easy to follow", "Rewards consistency", "Clear weekly standings"],
        cons: ["Less fair if teams play different numbers of games", "Draws need a points rule", "Long seasons need scheduling"],
    },
    {
        tag: "Fair with uneven games", name: "Win % League", to: LMS, ctaLabel: "Build a League",
        desc: "A win percentage league ranks players or teams by the proportion of matches they win rather than total points. It keeps standings fair when participants play different numbers of games.",
        steps: [
            "Players or teams join the league.",
            "Matches are played and results recorded.",
            "Each participant's win percentage is calculated from games played.",
            "The highest win percentage leads the table.",
        ],
        bestFor: "Flexible or social leagues where players play different numbers of matches, such as box or drop-in formats.",
        pros: ["Fair with uneven fixtures", "Great for flexible scheduling", "Simple to understand"],
        cons: ["Small samples can skew results", "Needs a minimum-games rule", "Less cumulative feel than points"],
    },
    {
        tag: "Engagement", name: "Ladder League", to: LADDER, ctaLabel: "Create Ladder",
        desc: "A ladder league ranks players on a ladder they climb by winning challenges or scheduled matches. Positions change throughout the season based on results.",
        steps: [
            "Players start on a ranked ladder.",
            "Players challenge those above them or play scheduled matches.",
            "Wins move players up; losses move them down.",
            "The ladder updates continuously through the season.",
        ],
        bestFor: "Racquet clubs and ongoing internal competitions focused on engagement and live rankings.",
        pros: ["Great for ongoing engagement", "Flexible, player-driven scheduling", "Always-current rankings"],
        cons: ["Needs clear challenge rules", "Relies on consistent participation", "No single finish line"],
    },
    {
        tag: "Best for big clubs", name: "Division Leagues", to: LMS, ctaLabel: "Build a League",
        desc: "Division leagues split a large membership into multiple divisions by skill level, with promotion and relegation between seasons so players always compete against similar opponents.",
        steps: [
            "Players or teams are placed into divisions by level.",
            "Each division runs its own league (points or win %).",
            "Standings decide promotion and relegation.",
            "Top players move up and others move down for the next season.",
        ],
        bestFor: "Clubs and academies with large memberships across a wide range of abilities.",
        pros: ["Balanced, competitive matches", "Scales to large memberships", "Clear progression path"],
        cons: ["More to manage each season", "Needs enough players per division", "Promotion rules must be defined"],
    },
    {
        tag: "Club favourite", name: "Box League", to: LMS, ctaLabel: "Build a League",
        desc: "A box league groups players into small boxes (mini round robins). After each cycle, top players move up a box and others move down, keeping matches competitive.",
        steps: [
            "Players are grouped into small boxes of similar level.",
            "Each box plays a round robin within a set period.",
            "Box standings decide who moves up or down.",
            "Boxes are reshuffled for the next cycle.",
        ],
        bestFor: "Ongoing club play that needs regular, competitive matches with periodic reshuffles.",
        pros: ["Always competitive matches", "Easy to schedule in cycles", "Good for mixed abilities"],
        cons: ["Needs regular cycles", "Requires enough players", "Movement rules to define"],
    },
    {
        tag: "Social", name: "Americano League", to: AMERICANO, ctaLabel: "Generate Americano",
        desc: "Americano leagues run social rotating sessions over time, with players changing partners and opponents and accumulating individual points across the season.",
        steps: [
            "Players join as individuals.",
            "Each session rotates partners and opponents.",
            "Individual points accumulate across sessions.",
            "Season standings rank players by total points.",
        ],
        bestFor: "Social padel, tennis and pickleball clubs that run regular member nights.",
        pros: ["Highly social", "Players meet everyone", "Great for member retention"],
        cons: ["Needs fairly even attendance", "Less team-based", "Scoring rules to explain"],
    },
];

const FAQS = [
    {
        q: 'What is the best league format?',
        a: <>The best league format depends on how your members play. Points leagues suit fixed season fixtures, win percentage leagues are fairer when players play unequal numbers of games, ladder and box leagues are great for ongoing club play, and division leagues are best for large memberships across skill levels.</>,
    },
    {
        q: 'What is the difference between a points league and a win percentage league?',
        a: <>A points league ranks by total points earned (for example three for a win), which rewards a full, fixed fixture list. A win percentage league ranks by the proportion of matches won, which stays fair when players play different numbers of games — ideal for flexible or social leagues.</>,
    },
    {
        q: 'How do promotion and relegation work in division leagues?',
        a: <>Each division runs its own standings. At the end of a season, the top players or teams in a division are promoted to a higher division and the bottom ones are relegated, so everyone keeps competing against similar opponents.</>,
    },
    {
        q: 'What is a box league?',
        a: <>A box league splits players into small boxes (mini round robins) of similar ability. Each cycle, the top players move up a box and others move down, keeping matches competitive and giving everyone a regular set of games.</>,
    },
    {
        q: 'Can ScorePal run multiple divisions and ranking types?',
        a: <>Yes. ScorePal supports points and win-percentage ranking, multiple divisions with promotion and relegation, ladders, box leagues and social formats — with automated fixtures, standings and live scores from one platform.</>,
    },
];

function Stars({count}: {count: number}) {
    return (
        <span className="text-[#f5a524] whitespace-nowrap tracking-wide" aria-label={`${count} out of 5`}>
            {"★".repeat(count)}<span className="text-[#1a1a2e]/20">{"★".repeat(5 - count)}</span>
        </span>
    );
}

function RouteComponent() {
    const [eventType, setEventType] = useState("competitive");
    const [participants, setParticipants] = useState("medium");

    const recommend = (): {format: string; reason: string} => {
        if (eventType === "social") {
            return {format: "Americano or Mexicano League", reason: "Best for social club nights where players rotate partners and opponents."};
        }
        if (eventType === "large") {
            return {format: "Division Leagues", reason: "Best for large memberships — divisions keep players competing against similar opponents."};
        }
        if (eventType === "ongoing") {
            return participants === "large"
                ? {format: "Ladder League", reason: "Best for ongoing rankings with many players and flexible scheduling."}
                : {format: "Box League", reason: "Best for regular club play with competitive, reshuffled groups."};
        }
        // competitive
        return participants === "large"
            ? {format: "Win % League", reason: "Best for big, flexible fixtures where players play different numbers of games."}
            : {format: "Points League", reason: "Best for a classic season with a fixed fixture list and a points table."};
    };

    const rec = recommend();

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>League Formats Explained: Points, Win %, Ladder, Divisions & More | ScorePal</title>
            <meta name="description" content="Compare the most popular league formats including Points leagues, Win Percentage leagues, Ladder leagues, Division leagues with promotion and relegation, Box leagues and social Americano leagues. Learn which league format is best and build yours instantly." />
            <meta name="keywords" content="league formats, league structure, points league, win percentage league, ladder league, division league, promotion and relegation, box league, americano league, sports league software" />
            <link rel="canonical" href={`${config.publicUrl}/league-formats/`} />
            <meta property="og:title" content="League Formats Explained | ScorePal" />
            <meta property="og:description" content="Compare Points, Win %, Ladder, Division and social league formats." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/league-formats/`} />
            <meta property="og:image" content="https://scorepal.co/images/league-formats-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="League Formats Explained | ScorePal" />
            <meta name="twitter:description" content="Compare the most popular league formats and build your league instantly." />

            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is the best league format?", "acceptedAnswer": {"@type": "Answer", "text": "It depends on how members play. Points leagues suit fixed season fixtures, win percentage leagues are fairer when players play unequal games, ladder and box leagues suit ongoing club play, and division leagues are best for large memberships across skill levels."}},
                    {"@type": "Question", "name": "What is the difference between a points league and a win percentage league?", "acceptedAnswer": {"@type": "Answer", "text": "A points league ranks by total points earned, which rewards a full fixed fixture list. A win percentage league ranks by the proportion of matches won, which stays fair when players play different numbers of games."}},
                    {"@type": "Question", "name": "How do promotion and relegation work in division leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Each division runs its own standings. At the end of a season the top players or teams are promoted and the bottom ones are relegated, so everyone keeps competing against similar opponents."}},
                    {"@type": "Question", "name": "What is a box league?", "acceptedAnswer": {"@type": "Answer", "text": "A box league splits players into small boxes of similar ability. Each cycle the top players move up a box and others move down, keeping matches competitive."}},
                    {"@type": "Question", "name": "Can ScorePal run multiple divisions and ranking types?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal supports points and win-percentage ranking, multiple divisions with promotion and relegation, ladders, box leagues and social formats with automated fixtures, standings and live scores."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "League Formats", item: "https://scorepal.co/league-formats/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    League Planning Guide
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    League Formats Explained
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Compare the most popular league formats, choose the best structure for your club and run
                                    points leagues, win percentage leagues, ladder leagues, division leagues with promotion
                                    and relegation, box leagues and social Americano leagues.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <a href="#formats" className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Explore Formats</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </a>
                                    <a href="#selector" className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Find Best Format
                                    </a>
                                </div>
                            </div>

                            {/* "At a glance" card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">League formats at a glance</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">
                                    Different clubs need different structures — some reward a full season, some stay fair with
                                    flexible fixtures, and others keep members engaged all year round.
                                </p>
                                <div className="space-y-3">
                                    {[
                                        {label: "Classic standings", value: "Points League"},
                                        {label: "Fair with uneven games", value: "Win % League"},
                                        {label: "Best for big clubs", value: "Division Leagues"},
                                        {label: "Best for engagement", value: "Ladder / Box"},
                                    ].map((row) => (
                                        <div key={row.label} className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-3">
                                            <strong className="font-inter text-white text-sm">{row.label}</strong>
                                            <span className="font-inter text-white/70 text-sm">{row.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What is a league format? */}
            <section className="py-12 lg:py-16 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="rounded-3xl bg-white p-8 lg:p-10 shadow-sm max-w-4xl mx-auto">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(26px,3.5vw,38px)] leading-tight tracking-wide mb-4">
                            What is a league format?
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/80 leading-relaxed mb-6">
                            A league format is the structure that determines how fixtures are scheduled, how standings are
                            ranked and how players progress over a season. The right choice depends on how often members
                            play, how competitive the league is and how big your membership is.
                        </p>
                        <ul className="space-y-3">
                            {[
                                "Use a points league for a classic season with a fixed fixture list.",
                                "Use a win % league when players play different numbers of games.",
                                "Use a ladder league for ongoing, player-driven rankings.",
                                "Use division leagues for large memberships with promotion and relegation.",
                                "Use a box league for regular, competitive club play with reshuffles.",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3 font-inter text-[#1a1a2e]">
                                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#082F8A]/10 text-[#082F8A] font-bold text-sm">✓</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Formats grid */}
            <section id="formats" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Popular League Formats
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Browse the most common league structures and build the format that suits your members, schedule and
                        the way you want to rank standings.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FORMATS.map((f) => (
                            <Link
                                key={f.name}
                                to={f.to as any}
                                className="group bg-white rounded-2xl p-6 shadow-sm border border-transparent hover:border-[#082F8A]/25 hover:-translate-y-1 transition flex flex-col justify-between min-h-[210px]"
                            >
                                <div>
                                    <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                    <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.name}</h3>
                                    <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.blurb}</p>
                                </div>
                                <span className="font-inter text-[#082F8A] font-bold text-sm mt-4 group-hover:underline">{f.cta} →</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Compare table */}
            <section id="compare" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Compare League Formats
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Quickly compare fairness, match volume and the best use case for each league format.
                    </p>

                    <div className="overflow-x-auto">
                        <div className="min-w-[760px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-[1.2fr_0.8fr_0.8fr_1.6fr_0.8fr] bg-[#F6F7FB] font-bebas text-lg text-[#1a1a2e]">
                                <div className="p-4 border-r border-[#d9dce7]">Format</div>
                                <div className="p-4 border-r border-[#d9dce7]">Fairness</div>
                                <div className="p-4 border-r border-[#d9dce7]">Match Volume</div>
                                <div className="p-4 border-r border-[#d9dce7]">Best For</div>
                                <div className="p-4">Build</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.name} className={`grid grid-cols-[1.2fr_0.8fr_0.8fr_1.6fr_0.8fr] border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-4 border-r border-[#d9dce7] font-semibold">{row.name}</div>
                                    <div className="p-4 border-r border-[#d9dce7]"><Stars count={row.fairness} /></div>
                                    <div className="p-4 border-r border-[#d9dce7]">{row.volume}</div>
                                    <div className="p-4 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.bestFor}</div>
                                    <div className="p-4">
                                        <Link to={row.to as any} className="font-semibold text-[#082F8A] hover:underline">Open →</Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Format guides */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        League Format Guides
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Learn how each league format works, when to use it, and the advantages and considerations of each structure.
                    </p>

                    <div className="space-y-6">
                        {GUIDES.map((g) => (
                            <article key={g.name} className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8">
                                <div>
                                    <span className="inline-flex rounded-full bg-[#EDEEF4] text-[#082F8A] px-3 py-1 text-xs font-bold mb-3">{g.tag}</span>
                                    <h3 className="font-inter text-[#1a1a2e] font-semibold text-xl mb-2">{g.name}</h3>
                                    <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed mb-4">{g.desc}</p>

                                    <ol className="space-y-2.5 mb-4">
                                        {g.steps.map((s, i) => (
                                            <li key={i} className="flex items-start gap-3 font-inter text-[#1a1a2e]/80 text-sm leading-relaxed">
                                                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#082F8A] text-white font-bold text-xs">{i + 1}</span>
                                                <span>{s}</span>
                                            </li>
                                        ))}
                                    </ol>

                                    <p className="font-inter text-[#1a1a2e]/80 text-sm leading-relaxed mb-5">
                                        <strong className="text-[#1a1a2e]">Best for:</strong> {g.bestFor}
                                    </p>

                                    <Link to={g.to as any} className="inline-flex items-center gap-2 bg-[#082F8A] text-white rounded-full pl-5 pr-1 py-1 font-semibold hover:bg-[#06246b] transition">
                                        <span className="font-inter text-sm font-medium">{g.ctaLabel}</span>
                                        <div className="bg-white rounded-full p-1">
                                            <ArrowUpRightIcon className="text-[#082F8A]" size={24} />
                                        </div>
                                    </Link>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="bg-[#F8FAFC] border border-[#e5e7eb] rounded-2xl p-5">
                                        <h4 className="font-inter font-semibold text-[#12b76a] mb-2">Advantages</h4>
                                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-[#1a1a2e]/80">
                                            {g.pros.map((p) => <li key={p}>{p}</li>)}
                                        </ul>
                                    </div>
                                    <div className="bg-[#F8FAFC] border border-[#e5e7eb] rounded-2xl p-5">
                                        <h4 className="font-inter font-semibold text-[#d92d20] mb-2">Considerations</h4>
                                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-[#1a1a2e]/80">
                                            {g.cons.map((c) => <li key={c}>{c}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Format selector */}
            <section id="selector" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <div>
                            <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-4">Format Selector</span>
                            <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-3">
                                Which league format should you use?
                            </h2>
                            <p className="font-inter text-white/75 text-base leading-relaxed">
                                Answer two quick questions and get a recommended league format for your club.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-6 lg:p-8">
                            <label htmlFor="eventType" className="block font-inter font-semibold text-[#1a1a2e] mb-2">What kind of league is it?</label>
                            <select
                                id="eventType"
                                value={eventType}
                                onChange={(e) => setEventType(e.target.value)}
                                className="w-full rounded-xl border border-[#e5e7eb] p-3.5 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                            >
                                <option value="competitive">Competitive season</option>
                                <option value="social">Social club nights</option>
                                <option value="ongoing">Ongoing club play</option>
                                <option value="large">Large multi-level membership</option>
                            </select>

                            <label htmlFor="participants" className="block font-inter font-semibold text-[#1a1a2e] mt-4 mb-2">How many participants?</label>
                            <select
                                id="participants"
                                value={participants}
                                onChange={(e) => setParticipants(e.target.value)}
                                className="w-full rounded-xl border border-[#e5e7eb] p-3.5 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                            >
                                <option value="small">Under 12</option>
                                <option value="medium">12 to 32</option>
                                <option value="large">32+</option>
                            </select>

                            <div className="mt-5 rounded-2xl bg-[#EDEEF4] border border-[#082F8A]/15 p-5">
                                <span className="font-inter text-sm text-[#1a1a2e]/70">Recommended format:</span>
                                <strong className="block font-bebas text-2xl text-[#082F8A] tracking-wide mt-1">{rec.format}</strong>
                                <p className="font-inter text-sm text-[#1a1a2e]/80 mt-2">{rec.reason}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Any League With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Build fixtures, manage registrations, rank standings by points or win percentage, run divisions and
                        ladders, collect scores and publish live results for clubs and leagues.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Points League", "Win % League", "Ladder League", "Division Leagues", "Box League", "Live Scores"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"League Format FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
